import { useAuth } from '@/lib/use-auth';
import { CheckCircle2, Landmark } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { type TutorProfileResponse } from '@/lib/schemas/tutor-profile';
import { SupportTooltip, LockedField } from './shared';

export function ProfileOverviewCard({ profile }: { profile: TutorProfileResponse }) {
  const { user } = useAuth();
  const initials = profile.displayName
    ? profile.displayName.trim().split(/\s+/).map((w) => w[0] ?? '').slice(0, 2).join('').toUpperCase()
    : `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.toUpperCase();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Profile Overview</CardTitle>
        <CardDescription>Some details can only be changed by contacting support.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Photo */}
        <div className="flex items-center gap-4">
          {profile.photoUrl ? (
            <img
              src={profile.photoUrl}
              alt="Profile photo"
              className="h-16 w-16 shrink-0 rounded-full object-cover ring-2 ring-brand-200 dark:ring-brand-800"
            />
          ) : (
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-brand-600 text-lg font-bold text-white">
              {initials}
            </div>
          )}
          <div>
            <p className="font-semibold text-foreground">{profile.displayName || user?.firstName}</p>
            <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
              <span>Profile photo</span>
              <SupportTooltip text="To update your profile photo, please contact support." />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <LockedField
            label="Email address"
            value={user?.email ?? ''}
            tooltip="To change your email address, please contact support."
          />
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-medium text-foreground">Bank account</span>
              <SupportTooltip text="To update your bank account details, please contact support." />
            </div>
            <div className="flex h-10 w-full items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 text-sm dark:border-slate-700 dark:bg-slate-800 cursor-not-allowed select-none">
              {profile.bankDetailsOnFile ? (
                <>
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                  <span className="text-emerald-700 dark:text-emerald-400">On file</span>
                </>
              ) : (
                <>
                  <Landmark className="h-4 w-4 shrink-0 text-slate-400" />
                  <span className="text-slate-400">Not on file</span>
                </>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
